import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CNav,
  CNavItem,
  CNavLink,
  CSmartTable,
} from '@coreui/react-pro'
import moment from 'moment'
import ToastComponent from 'src/components/common/TaostComponent.js'
import GameActivationService from 'src/service/GameActivationService'
import MarkPaidModal from './MarkPaidModal'

const gameTypeLabels = {
  1: 'Season Game',
  2: 'Tournament (Leagues)',
  3: 'Tournament (Indoor Leagues)',
  4: 'Match Contest',
}

// 'season_package' -> 'Season Package'
const humanize = (value) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null

const teamRange = (item) => {
  if (!item.max_teams) return null
  return item.min_teams && item.min_teams !== item.max_teams
    ? `${item.min_teams}–${item.max_teams} teams`
    : `Up to ${item.max_teams} teams`
}

const formatAmount = (amount) => `$${Number(amount || 0).toFixed(2)}`

const GameActivations = () => {
  const [status, setStatus] = useState('unpaid')
  const [loading, setLoading] = useState(true)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [plans, setPlans] = useState([])
  const [unpaidCount, setUnpaidCount] = useState(0)

  const [selected, setSelected] = useState(null)

  const isUnpaid = status === 'unpaid'

  const columns = [
    { label: 'Game', key: 'game_name' },
    { label: 'Game Admin', key: 'admin_name' },
    { label: 'Plan', key: 'product_name', sorter: false },
    { label: 'Amount', key: 'amount', filter: false },
    isUnpaid
      ? { label: 'Signed Up', key: 'created_at', filter: false }
      : { label: 'Paid On', key: 'updated_at', filter: false },
    isUnpaid
      ? { key: 'action', label: 'Action', filter: false, sorter: false }
      : { key: 'paid_via', label: 'Paid Via', filter: false, sorter: false },
  ]

  useEffect(() => {
    fetchData()
  }, [status, activePage, columnFilter, columnSorter, itemsPerPage])

  const fetchData = () => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    params.append('status', status)
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    GameActivationService.getActivationList(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setPlans(result.data)
        setUnpaidCount(result.unpaid_count || 0)
      })
      .catch((err) =>
        ToastComponent(err?.response?.data?.message || 'Unable to load games', 'error'),
      )
      .finally(() => setLoading(false))
  }

  const switchTab = (tab) => {
    if (tab === status) return
    setPlans([])
    setActivePage(1)
    setColumnFilter([])
    setColumnSorter(null)
    setStatus(tab)
  }

  return (
    <>
      <CCard className="mb-4">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <strong>Activate Games</strong>
          {unpaidCount > 0 && (
            <CBadge color="danger" shape="rounded-pill">
              {unpaidCount} awaiting payment
            </CBadge>
          )}
        </CCardHeader>
        <CCardBody>
          <p className="text-medium-emphasis small mb-3">
            Games below picked a pricing plan at signup but haven&apos;t paid through Stripe. Once
            you confirm payment, mark the game as paid and its admin can activate the game.
          </p>

          <CNav variant="tabs" className="mb-3">
            <CNavItem>
              <CNavLink
                active={isUnpaid}
                onClick={() => switchTab('unpaid')}
                style={{ cursor: 'pointer' }}
              >
                Unpaid{' '}
                {unpaidCount > 0 && (
                  <CBadge color="danger" className="ms-1">
                    {unpaidCount}
                  </CBadge>
                )}
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                active={!isUnpaid}
                onClick={() => switchTab('paid')}
                style={{ cursor: 'pointer' }}
              >
                Paid
              </CNavLink>
            </CNavItem>
          </CNav>

          <CSmartTable
            key={status}
            columns={columns}
            columnFilter={{ external: true }}
            columnSorter={{ external: true }}
            scopedColumns={{
              game_name: (item) => (
                <td>
                  <div className="fw-semibold">{item.game_name || '-'}</div>
                  <div className="small text-medium-emphasis">
                    #{item.game_id}
                    {gameTypeLabels[item.game_type] && ` · ${gameTypeLabels[item.game_type]}`}
                  </div>
                </td>
              ),
              admin_name: (item) => (
                <td>
                  <div>{item.admin_name || '-'}</div>
                  <div className="small text-medium-emphasis">{item.admin_email}</div>
                  {item.club_name && (
                    <div className="small text-medium-emphasis">{item.club_name}</div>
                  )}
                </td>
              ),
              product_name: (item) => (
                <td>
                  <div>{item.product_name || '-'}</div>
                  <div className="small text-medium-emphasis">
                    {[
                      humanize(item.service_package),
                      teamRange(item),
                      humanize(item.competition_duration),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </td>
              ),
              amount: (item) => <td className="fw-semibold">{formatAmount(item.amount)}</td>,
              created_at: (item) => (
                <td>
                  <div>{item.created_at ? moment(item.created_at).format('D MMM YYYY') : '-'}</div>
                  {item.created_at && (
                    <div className="small text-medium-emphasis">
                      {moment(item.created_at).fromNow()}
                    </div>
                  )}
                </td>
              ),
              updated_at: (item) => (
                <td>{item.updated_at ? moment(item.updated_at).format('D MMM YYYY') : '-'}</td>
              ),
              paid_via: (item) => (
                <td>
                  {item.stripe_payment_intent_id ? (
                    <CBadge color="info">Stripe</CBadge>
                  ) : (
                    <CBadge color="secondary">Manual</CBadge>
                  )}
                </td>
              ),
              action: (item) => (
                <td>
                  <CButton color="success" size="sm" onClick={() => setSelected(item)}>
                    Mark Paid
                  </CButton>
                </td>
              ),
            }}
            items={plans?.data}
            itemsPerPage={itemsPerPage}
            itemsPerPageSelect
            loading={loading}
            noItemsLabel={isUnpaid ? 'No unpaid games — all caught up 🎉' : 'No paid games yet'}
            pagination={{ external: true }}
            paginationProps={{
              activePage: activePage,
              pages: Math.ceil(plans?.total / itemsPerPage) || 1,
            }}
            tableProps={{ hover: true, responsive: true, align: 'middle' }}
            onActivePageChange={(activePage) => setActivePage(activePage)}
            onColumnFilterChange={(filter) => {
              setActivePage(1)
              setColumnFilter(filter)
            }}
            onItemsPerPageChange={(itemsPerPage) => {
              setActivePage(1)
              setItemsPerPage(itemsPerPage)
            }}
            onSorterChange={(sorter) => setColumnSorter(sorter)}
          />
        </CCardBody>
      </CCard>

      {selected && (
        <MarkPaidModal
          key={selected.id}
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null)
            fetchData()
          }}
        />
      )}
    </>
  )
}

export default GameActivations
